
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Modal, ModalVariant, Button } from '@patternfly/react-core';

export interface TokenData {
  iamAccessToken: string;
  iamTokenExpiresAt: string;
  azureAccessToken: string;
  azureRefreshToken: string;
  userInfo: {
    email: string;
    name: string;
  };
  azureTokenExpiresAt: string;
}

export interface AuthContextType {
  tokenData: TokenData | null;
  setTokenData: (data: TokenData | null) => void;
  getIamAccessToken: () => string | null;
  getAzureAccessToken: () => string | null;
  getAuthHeader: () => { Authorization: string } | null;
  isTokenExpired: () => boolean;
  isAzureTokenExpired: () => boolean;
  clearTokens: () => void;
  getUserInfo: () => { email: string; name: string } | null;
  refreshTokens: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);

  const getIamAccessToken = () => tokenData?.iamAccessToken || null;
  
  const getAzureAccessToken = () => tokenData?.azureAccessToken || null;

  const getAuthHeader = () => {
    const token = getIamAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  const isTokenExpired = () => {
    if (!tokenData?.iamTokenExpiresAt) return true;
    return new Date() >= new Date(tokenData.iamTokenExpiresAt);
  };

  const isAzureTokenExpired = () => {
    if (!tokenData?.azureTokenExpiresAt) return true;
    return new Date() >= new Date(tokenData.azureTokenExpiresAt);
  };

  const clearTokens = () => setTokenData(null);

  const getUserInfo = () => tokenData?.userInfo || null;

  /**
   * Refresh tokens using Azure refresh token
   * Returns true if successful, false otherwise
   */
  const refreshTokens = async (): Promise<boolean> => {
    if (isRefreshing) {
      console.log('Token refresh already in progress');
      return false;
    }

    if (!tokenData?.azureRefreshToken) {
      console.error('No refresh token available');
      return false;
    }

    setIsRefreshing(true);
    try {
      // Get portal and IAM env from query params if available
      // Decode base64 encoded data parameter
      const urlSearchParams = new URLSearchParams(window.location.search);
      const encodedData = urlSearchParams.get('data') || '';
      const decodedData = encodedData ? atob(encodedData) : '';
      const urlParams = new URLSearchParams(decodedData);
      const portalEnv = urlParams.get('portalEnv') || '';
      const iamEnv = urlParams.get('iamEnv') || '';

      const response = await fetch(
        `v2/atlas/exchange-token?portalEnv=${encodeURIComponent(portalEnv)}&iamEnv=${encodeURIComponent(iamEnv)}`,
        {
          headers: {
            'Authorization': `Bearer ${tokenData.azureRefreshToken}`
          }
        }
      );

      if (!response.ok) {
        console.error('Token refresh failed:', response.status);
        if (response.status === 401) {
          clearTokens();
          setShowExpiryDialog(true);
        }
        return false;
      }

      const newTokenData = await response.json();
      setTokenData(newTokenData);
      console.log('Tokens refreshed successfully');
      return true;
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      clearTokens();
      setShowExpiryDialog(true);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-refresh tokens before they expire (5 minutes before expiry)
  // Only works if refresh token is available (exchange-token flow)
  React.useEffect(() => {
    if (!tokenData?.iamTokenExpiresAt || !tokenData?.azureRefreshToken) {
      // No refresh token available (check-token flow)
      // Will not auto-refresh, just monitor expiration
      return;
    }

    const checkAndRefresh = () => {
      // Check IAM token expiry
      const iamExpiresAt = new Date(tokenData.iamTokenExpiresAt);
      const now = new Date();
      const fiveMinutesBeforeIamExpiry = new Date(iamExpiresAt.getTime() - 5 * 60 * 1000);

      // Check Azure token expiry
      let shouldRefreshAzure = false;
      if (tokenData.azureTokenExpiresAt) {
        const azureTokenExpiry = new Date(tokenData.azureTokenExpiresAt);
        const fiveMinutesBeforeAzureExpiry = new Date(azureTokenExpiry.getTime() - 5 * 60 * 1000);
        shouldRefreshAzure = now >= fiveMinutesBeforeAzureExpiry;
      }

      // Refresh if either IAM or Azure token is expiring soon
      if (now >= fiveMinutesBeforeIamExpiry || shouldRefreshAzure) {
        console.log('Token expiring soon, attempting refresh...', {
          iamExpiring: now >= fiveMinutesBeforeIamExpiry,
          azureExpiring: shouldRefreshAzure
        });
        refreshTokens();
      }
    };

    // Check every minute
    const interval = setInterval(checkAndRefresh, 60 * 1000);
    
    // Check immediately on mount
    checkAndRefresh();

    return () => clearInterval(interval);
  }, [tokenData?.iamTokenExpiresAt, tokenData?.azureRefreshToken, tokenData?.azureTokenExpiresAt]);

  // Monitor token expiration for check-token flow (no auto-refresh)
  React.useEffect(() => {
    // Only monitor if no refresh token (check-token flow)
    if (!tokenData?.iamTokenExpiresAt || tokenData?.azureRefreshToken) {
      return;
    }

    const checkExpiration = () => {
      const now = new Date();
      const expiresAt = new Date(tokenData.iamTokenExpiresAt);
      
      // Token has expired
      if (now >= expiresAt) {
        console.log('Token expired in check-token flow');
        clearTokens();
        setShowExpiryDialog(true);
      }
    };

    // Check immediately
    checkExpiration();
    
    // Check every 30 seconds
    const interval = setInterval(checkExpiration, 30 * 1000);
    
    return () => clearInterval(interval);
  }, [tokenData?.iamTokenExpiresAt, tokenData?.azureRefreshToken]);

  return (
    <AuthContext.Provider
      value={{
        tokenData,
        setTokenData,
        getIamAccessToken,
        getAzureAccessToken,
        getAuthHeader,
        isTokenExpired,
        isAzureTokenExpired,
        clearTokens,
        getUserInfo,
        refreshTokens,
      }}
    >
      <Modal
        variant={ModalVariant.small}
        title="Session Expired"
        isOpen={showExpiryDialog}
        onClose={() => {
          setShowExpiryDialog(false);
          window.location.reload();
        }}
        actions={[
          <Button
            key="reload"
            variant="primary"
            onClick={() => {
              setShowExpiryDialog(false);
              window.location.reload();
            }}
          >
            Reload Page
          </Button>
        ]}
      >
        Your session has expired. Please reload the page from the Portal.
      </Modal>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
